import { PulseBaseProvider } from '../../../../base-classes';
import {
    IGetStationObservationsParams
} from '../../../actions/WeatherStationObservations/SelectedStation/selectedStationObservationsAction';
import type { IPulseRequestConfiguration } from '../../../types/PulseInterfaces';


const _currentStationObservationsUri = '/api/obs/surface/current/v7';
const _currentStationObservationsCacheKey = 'currentStationObservations';

export class SelectedStationObservationsProvider extends PulseBaseProvider {
    constructor(baseUrl, authId, secret, options, logger) {
        super(baseUrl, authId, secret, options, logger);
        this.storage.sync[_currentStationObservationsCacheKey] = this.currentStationObservationsSyncer.bind(this);
    }

    async currentStationObservationsSyncer(params) {
        const { id, syncParams, resolve, reject } = params;

        const queryParams = {
            ...syncParams.stdParameters,
            cachedOnly: syncParams.isCached,
            directionUnit: syncParams.directionUnit,
            disableRollover: syncParams.disableRollover,
            distanceUnit: syncParams.distanceUnit,
            fml: syncParams.fml,
            pageNumber: 1,
            pageSize: 1,
            precipitationRateUnit: syncParams.precipitationRateUnit,
            precipitationUnit: syncParams.precipitationUnit,
            pressureUnit: syncParams.pressureUnit,
            roundingProfile: syncParams.roundingProfile,
            searchRadius: syncParams.searchRadius,
            stationState: syncParams.stationState,
            stationType: syncParams.stationType,
            speedUnit: syncParams.speedUnit,
            temperatureUnit: syncParams.temperatureUnit,
            verbose: syncParams.verbose,
            includePrecip: syncParams.includePrecip
        };

        let apiPath = _currentStationObservationsUri;

        /** Configure the url path based on the input params, we can make either
         * -- 1. Request for a specific station using the provider and station ids, requires **both** ProviderId and StationId
         * -- 2. Request for a specific from a location, using latitude and longitude
         */
        if (syncParams.providerId && syncParams.stationId) {
            apiPath += `/providerId=${syncParams.providerId}/stationId=${syncParams.stationId}`;

            // We should not include location with a request for a specific station, but if we do - we need to add them as query parameters instead.
            if (syncParams.latitude && syncParams.longitude) {
                queryParams.locationType = 'latitudelongitude';
                queryParams.location = `${syncParams.latitude},${syncParams.longitude}`;
            }
        }
        else if (syncParams.latitude && syncParams.longitude) {
            apiPath += `/locationType=latitudelongitude/location=${syncParams.latitude},${syncParams.longitude}`;
        }

        return await super.syncData(syncParams.key, id, queryParams, apiPath, resolve, reject, null, syncParams.pulseApi);
    }

    /**
     * Fetches the observations from storage if they exist and are still valid or fetches new observations if needed. 
     * @param apiParameters
     * -- All of the parameters for the Pulse Observations API to be aware of when requesting observations for a station
     * -- API Docs are available here -- http://currentstationobsapi-obs.weatherbug.net/swagger
     * @param requestConfiguration
     * -- The necessary configuration for the store/provider and pulse to determine things like
     * -- 1. Is this a valid request?
     * -- 2. Was this recently fetched and cachable result can be returned or should this be refreshed?
     * -- 3. Etc ...
     */
    async fetchCurrentStationObservations({ apiParameters, requestConfiguration }: {
        apiParameters: IGetStationObservationsParams;
        requestConfiguration: IPulseRequestConfiguration;
    }) {
        const syncParams = {
            cachedOnly: apiParameters.isCached ?? false,
            directionUnit: apiParameters.directionUnit ?? 1,
            disableRollover: apiParameters.disableRollover ?? false,
            distanceUnit: apiParameters.distanceUnit ?? 1,
            fml: apiParameters.fml ?? false,
            latitude: apiParameters.latitude,
            longitude: apiParameters.longitude,
            precipitationRateUnit: apiParameters.precipitationRateUnit ?? 1,
            precipitationUnit: apiParameters.precipitationUnit ?? 1,
            pressureUnit: apiParameters.pressureUnit ?? 1,
            providerId: apiParameters.providerId,
            roundingProfile: apiParameters.roundingProfile,
            searchRadius: apiParameters.searchRadius || 20,
            speedUnit: apiParameters.speedUnit ?? 1,
            stationId: apiParameters.stationId,
            temperatureUnit: apiParameters.temperatureUnit ?? 1,
            verboseResponseKeys: requestConfiguration.verboseResponseKeys ?? false,
            includePrecip: apiParameters?.includePrecip ?? true
        };
        const id = JSON.stringify(syncParams);
        
        if (requestConfiguration.forceRefreshOfCachedData) {
            super.removeFromStorage(_currentStationObservationsCacheKey, id);
        }
        return await super.loadFromStorage(_currentStationObservationsCacheKey, id, syncParams);
    }
}
